import { Injectable } from '@angular/core';
import { DisplayNames } from 'common/class/data/model/displayNames';
import { Organization } from 'common/class/data/model/organization';
import { Source } from 'common/class/data/model/source';
import { Logger } from 'common/utility/logger/logger';
import { Strings } from 'common/utility/string/string';
import { BehaviorSubject, Observable, take, tap } from 'rxjs';
import { EdgeDevice } from 'services/edge-device/edge-device.service';
import { ApiController } from 'services/http/api-controller';
import { Api } from 'services/http/api-endpoints';
import { HttpService } from 'services/http/http.service';
import { Endpoint } from 'services/http/interface/endpoint';
import { Route } from 'services/http/routes';
import { StorageService } from 'services/storage/storage.service';
import { TranslationService } from 'services/translation/translation.service';
import { OrganizationResponse } from './response/organization-info';


export interface CustomerOrganization {
  id?: string
  identifier?: string
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {

  private organization: Organization | undefined = undefined
  private edgeDevices: BehaviorSubject<Array<EdgeDevice>> = new BehaviorSubject<Array<EdgeDevice>>([])

  public get Identifier(): string {
    return StorageService.OrganizationIdentifier ?? Strings.Empty
  }

  public set Identifier(value: string | null) {
    StorageService.OrganizationIdentifier = value
    if (!!value) {
      this.fetchSources()
      this.fetchInformation()
      this.fetchEdgeDevices()
    } else {
      this.Sources = null
    }
  }

  public get EdgeDevicesChanged(): Observable<Array<EdgeDevice>> {
    return this.edgeDevices.asObservable()
  }

  public get Sources(): Array<Source> {
    return StorageService.OrganizationSource ?? []
  }

  public list(): Observable<Array<CustomerOrganization>> {
    return this.httpService.request(Api.organization, Route.Organization.List, null, null)
  }

  private set Sources(value: Array<Source> | null) {
    StorageService.OrganizationSource = value
  }

  public sourcesSet(id: string, sources: Array<string>): Observable<any> {
    const action: string = Route.Organization.Sources.Set;
    const endpoint: Endpoint = Api.getEndpoint(Api.organization, action);
    return this.httpService.request(Api.organization, action, Api.createParameters(endpoint.route, [id]), sources)
  }

  public get OrganizationInformation(): Organization | undefined {
    if (!!this.organization) return this.organization
    const organization: any = StorageService.OrganizationInformation ?? undefined
    if (!!organization) {
      this.organization = new Organization(new DisplayNames(organization.displayNames.names))
    }
    return this.organization
  }

  public getEdgeDevices(id: string): Observable<Array<EdgeDevice>> {
    const action: string = Route.Organization.EdgeDevices.Fetch;
    const endpoint: Endpoint = Api.getEndpoint(Api.organization, action);
    return this.httpService.request(Api.organization, action, Api.createParameters(endpoint.route, [id])).pipe(
      tap((devices: Array<EdgeDevice>) => this.edgeDevices.next(devices))
    )
  }

  public setEdgeDevices(id: string, edgeIds: Array<string>): Observable<Array<EdgeDevice>> {
    const action: string = Route.Organization.EdgeDevices.Set;
    const endpoint: Endpoint = Api.getEndpoint(Api.organization, action);
    return this.httpService.request(Api.organization, action, Api.createParameters(endpoint.route, [id]), edgeIds).pipe(
      tap((devices: Array<EdgeDevice>) => this.edgeDevices.next(devices))
    )
  }

  public get Name(): string {
    return this.OrganizationInformation?.displayNames?.getName(TranslationService.CurrentLanguage.CountryCode) ?? Strings.Empty
  }

  private set OrganizationInformation(value: Organization | undefined) {
    this.organization = undefined
    StorageService.OrganizationInformation = value ?? null
  }

  constructor(
    private httpService: HttpService,
  ) { }

  private fetchSources(): void {
    this.getSources().pipe(
      take(1)
    ).subscribe((sources: Array<Source>) => {
      this.Sources = sources
      Logger.debug("Retrieved sources for Organization", sources)
    })
  }

  private fetchEdgeDevices(): void {
    this.getEdgeDevices(this.Identifier).pipe(
      take(1)
    ).subscribe((devices: Array<EdgeDevice>) => {
      Logger.debug("Retrieved Edge Devices for Organization", devices)
    })
  }


  private fetchInformation(): void {
    this.getOrganizationInformation().pipe(
      take(1)
    ).subscribe((info: OrganizationResponse) => {
      if (info.displayNames) {
        this.OrganizationInformation = new Organization(this.convertDisplayNames(info.displayNames))
        Logger.debug("Retrieved Information for Organization", info)
      }
    })
  }

  private convertDisplayNames(displayNames: string): DisplayNames {
    const names: any = JSON.parse(displayNames)

    const returnValue: DisplayNames = !!names
      ? new DisplayNames(new Map(Object.entries(names)))
      : DisplayNames.create()
    return returnValue
  }




  private getOrganizationInformation(): Observable<any> {
    if (!this.Identifier) {
      throw new Error(`Organization identifier has not been set`)
    }
    Logger.debug(`Fetching Information for Organization: ${this.Identifier}`)
    const controller: ApiController = Api.organization
    const action: string = Route.Organization.Information
    const endpoint: Endpoint = Api.getEndpoint(controller, action);

    return this.httpService.request(controller, action, Api.createParameters(endpoint.route, [this.Identifier]))
  }

  private getSources(): Observable<any> {
    if (!this.Identifier) {
      throw new Error(`Organization identifier has not been set`)
    }
    return this.getSourcesByOrgId(this.Identifier)
  }

  public getSourcesByOrgId(id: string): Observable<any> {

    Logger.debug(`Fetching sources for Organization: ${id}`)
    const controller: ApiController = Api.organization
    const action: string = Route.Organization.Sources.Fetch;
    const endpoint: Endpoint = Api.getEndpoint(controller, action);

    return this.httpService.request(controller, action, Api.createParameters(endpoint.route, [id]))
  }


}
